import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Star,
  Menu, X
} from 'lucide-react';
import Footer from '../components/Footer';
import KolMyGlassLogo from '../components/KolMyGlassLogo';
import LandingHeroBackground from '../components/LandingHeroBackground';
import LandingMarketingHero from '../components/LandingMarketingHero';
import Parallax from '../components/Parallax';
import Reveal from '../components/Reveal';

const FEATURES = [
  {
    icon: '/images/icon-search.svg',
    title: 'Influencer Discovery',
    description: 'Find the perfect influencers across Instagram, TikTok, and YouTube with our advanced search filters and AI-powered matching.'
  },
  {
    icon: '/images/icon-analysis.svg',
    title: 'Analytics & Insights',
    description: 'Get comprehensive analytics on influencer performance, audience demographics, and engagement metrics.'
  },
  {
    icon: '/images/icon-dashboard.svg',
    title: 'Campaign Tracking',
    description: 'Track your campaign performance in real-time with detailed reports and ROI analysis.'
  },
  {
    icon: '/images/icon-competitor.svg',
    title: 'KOL Management',
    description: 'Manage your relationships with Key Opinion Leaders through our integrated CRM system.'
  },
  {
    icon: '/images/icon-trend.svg',
    title: 'Audience Targeting',
    description: 'Reach the right audience with precise demographic and psychographic targeting capabilities.'
  },
  {
    icon: '/images/icon-optimize.svg',
    title: 'AI-Powered Insights',
    description: 'Leverage AI to predict campaign success and optimize your influencer marketing strategy.'
  }
];

const SOLUTIONS_FOR_BRANDS = [
  {
    icon: '/images/icon-perfect-match.svg',
    title: 'Find Perfect Match',
    description: 'AI-powered matching connects you with influencers whose audience aligns with your target market.'
  },
  {
    icon: '/images/icon-manage-campaigns.svg',
    title: 'Manage Campaigns',
    description: 'End-to-end campaign management from outreach to reporting, all in one platform.'
  },
  {
    icon: '/images/icon-measure-roi.svg',
    title: 'Measure ROI',
    description: 'Track conversions, engagement, and revenue with advanced attribution modeling.'
  }
];

const SOLUTIONS_FOR_AGENCIES = [
  {
    icon: '/images/icon-scale-operations.svg',
    title: 'Scale Operations',
    description: 'Automate outreach, negotiations, and payments to manage hundreds of influencer relationships.'
  },
  {
    icon: '/images/icon-client-reporting.svg',
    title: 'Client Reporting',
    description: 'Generate beautiful, automated reports to keep your clients informed and impressed.'
  },
  {
    icon: '/images/icon-team-collaboration.svg',
    title: 'Team Collaboration',
    description: 'Built-in workflows for agencies to collaborate on campaigns and share resources.'
  }
];

const PROCESS_STEPS = [
  {
    step: '01',
    title: 'Connect Your Data',
    description: 'Import your existing influencer lists or use our database of 300M+ creators.'
  },
  {
    step: '02',
    title: 'AI Analysis',
    description: 'Our AI analyzes audience quality, engagement patterns, and brand fit.'
  },
  {
    step: '03',
    title: 'Launch Campaigns',
    description: 'Execute campaigns with automated outreach and real-time optimization.'
  }
];

const TESTIMONIALS = [
  {
    quote: 'kol.my transformed how we approach influencer marketing. The AI matching saved us countless hours and improved our campaign ROI by 40%.',
    author: 'Sarah Johnson',
    role: 'Marketing Director',
    company: 'TechCorp',
    initials: 'SJ'
  },
  {
    quote: 'The platform\'s analytics are incredibly detailed. We can now prove the value of our influencer campaigns to stakeholders with real data.',
    author: 'Michael Chen',
    role: 'Head of Brand',
    company: 'BeautyBrand',
    initials: 'MC'
  }
];

export default function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'brands' | 'agencies'>('brands');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#05050d] font-sans text-white">
      <div className="pointer-events-none fixed inset-0 z-0">
        <LandingHeroBackground appearance="dark" />
      </div>
      <div className="relative z-10">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0a0a0f]/90 backdrop-blur-xl border-b border-white/10' : ''} py-5`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0 outline-none ring-offset-2 ring-offset-[#0a0a0f] focus-visible:ring-2 focus-visible:ring-violet-500/80">
            <KolMyGlassLogo variant="nav" forDarkBackground />
          </Link>
          
          <ul className="hidden md:flex items-center gap-10">
            <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Solutions</a></li>
            <li><a href="#features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</a></li>
            <li><a href="#testimonials" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Testimonials</a></li>
            <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Pricing</a></li>
          </ul>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/login" className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-violet-600 to-blue-500 rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/20">
              Get Started
            </Link>
          </div>

          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#0a0a0f] border-b border-white/10 p-6">
            <ul className="flex flex-col gap-4 mb-6">
              <li><a href="#solutions" className="text-gray-400 hover:text-white transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Solutions</a></li>
              <li><a href="#features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Features</a></li>
              <li><a href="#testimonials" className="text-gray-400 hover:text-white transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Testimonials</a></li>
              <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Pricing</a></li>
            </ul>
            <div className="flex flex-col gap-3">
              <Link to="/login" className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-violet-600 to-blue-500 rounded-lg text-center hover:opacity-90 transition-opacity">
                Get Started
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section (odd #1 — transparent, stagnant abstract bg shows through) */}
      <section className="relative z-10 flex min-h-screen items-center justify-center overflow-hidden pt-20">
        <Parallax speed={0.28} fade={1.1} clamp={240} className="relative z-10 w-full">
          <LandingMarketingHero variant="marketing" />
        </Parallax>
      </section>

      {/* Why kol.my Section (even #2 — translucent dark card scrolls with content) */}
      <section className="relative z-10 py-24 bg-[#0a0f1e]/80 backdrop-blur-sm border-y border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal direction="right">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Why Choose{' '}
                  <span className="bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-transparent">
                    kol.my
                  </span>
                  ?
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-8">
                  myKOL is a comprehensive Influencer Marketing AI Platform that helps brands, agencies, and creators in Malaysia and across Southeast Asia discover, analyze, and collaborate with influencers. With access to 300M+ influencer profiles and 6B+ real-time social media data points, we empower marketing teams to make data-driven decisions, streamline campaign management, and achieve measurable ROI.
                </p>
                <p className="text-gray-400 text-lg leading-relaxed mb-8">
                  Our platform supports multi-channel campaigns across Instagram, TikTok, YouTube, Facebook, and Twitter with advanced analytics, automated workflows, and AI-driven insights.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>300M+ Influencer Profiles</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>6B+ Data Points</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Multi-Platform Support</span>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal direction="left" delay={120}>
              <Parallax speed={0.2} clamp={140} className="relative">
                <div className="bg-gradient-to-br from-violet-600/20 to-blue-500/20 border border-white/10 rounded-2xl p-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <div className="text-3xl font-bold text-violet-400 mb-2">300M+</div>
                      <div className="text-sm text-gray-400">Influencer Profiles</div>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <div className="text-3xl font-bold text-blue-400 mb-2">6B+</div>
                      <div className="text-sm text-gray-400">Data Points</div>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <div className="text-3xl font-bold text-emerald-400 mb-2">50K+</div>
                      <div className="text-sm text-gray-400">Brands Served</div>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <div className="text-3xl font-bold text-amber-400 mb-2">120+</div>
                      <div className="text-sm text-gray-400">Countries</div>
                    </div>
                  </div>
                </div>
                <Parallax speed={-0.45} clamp={140} className="pointer-events-none absolute -top-4 -right-4">
                  <div className="w-24 h-24 bg-violet-500/30 rounded-full blur-2xl" />
                </Parallax>
                <Parallax speed={0.55} clamp={140} className="pointer-events-none absolute -bottom-4 -left-4">
                  <div className="w-32 h-32 bg-blue-500/30 rounded-full blur-2xl" />
                </Parallax>
              </Parallax>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Solutions Section (odd #3 — transparent, stagnant abstract bg shows through) */}
      <section id="solutions" className="relative z-10 py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative">
          <Reveal direction="up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Solutions for Every Need
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Whether you're a brand looking to collaborate with influencers or an agency managing multiple campaigns, we have you covered.
              </p>
            </div>
          </Reveal>

          <Reveal direction="up" delay={120}>
            <div className="flex justify-center gap-2 mb-12">
              <button
                onClick={() => setActiveTab('brands')}
                className={`px-8 py-3 rounded-full text-sm font-semibold transition-all ${
                  activeTab === 'brands'
                    ? 'bg-gradient-to-r from-violet-600 to-blue-500 text-white'
                    : 'border border-white/10 text-gray-400 hover:text-white'
                }`}
              >
                For Brands
              </button>
              <button
                onClick={() => setActiveTab('agencies')}
                className={`px-8 py-3 rounded-full text-sm font-semibold transition-all ${
                  activeTab === 'agencies'
                    ? 'bg-gradient-to-r from-violet-600 to-blue-500 text-white'
                    : 'border border-white/10 text-gray-400 hover:text-white'
                }`}
              >
                For Agencies
              </button>
            </div>
          </Reveal>

          <div key={activeTab} className="grid md:grid-cols-3 gap-6">
            {(activeTab === 'brands' ? SOLUTIONS_FOR_BRANDS : SOLUTIONS_FOR_AGENCIES).map((item, index) => (
              <Reveal key={`${activeTab}-${index}`} direction="up" delay={index * 100}>
                <div
                  className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:-translate-y-1 hover:border-white/20 transition-all duration-300 h-full"
                >
                  <div className="w-14 h-14 bg-gradient-to-r from-violet-600 to-blue-500 rounded-xl flex items-center justify-center mb-5">
                    <img src={item.icon} alt={item.title} className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI Campaigns Process (even #4 — translucent dark card) */}
      <section className="relative z-10 py-24 bg-[#0a0f1e]/80 backdrop-blur-sm border-y border-white/5 overflow-hidden">
        <Parallax speed={0.55} clamp={260} className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2">
          <div className="w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-3xl" />
        </Parallax>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Reveal direction="up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                AI-Powered Campaign Process
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Streamline your influencer marketing with our intelligent workflow.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-10 relative">
            {PROCESS_STEPS.map((item, index) => (
              <Reveal key={index} direction="up" delay={index * 140}>
                <div className="text-center relative">
                  {index < PROCESS_STEPS.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-violet-500 to-blue-500" />
                  )}
                  <div className="w-20 h-20 bg-gradient-to-r from-violet-600 to-blue-500 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg shadow-violet-500/30">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed max-w-xs mx-auto">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section (odd #5 — transparent, stagnant abstract bg shows through) */}
      <section id="features" className="relative z-10 py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative">
          <Reveal direction="up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Powerful Features
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Everything you need to succeed in influencer marketing.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <Reveal key={index} direction="up" delay={(index % 3) * 100 + Math.floor(index / 3) * 60}>
                <div
                  className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm hover:-translate-y-1 hover:border-white/20 transition-all duration-300 h-full"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-blue-500/20 border border-white/10 rounded-2xl flex items-center justify-center mb-5">
                    <img src={feature.icon} alt={feature.title} className="w-10 h-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials (even #6 — translucent dark card) */}
      <section id="testimonials" className="relative z-10 py-24 bg-[#0a0f1e]/80 backdrop-blur-sm border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal direction="up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Trusted by Industry Leaders
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                See what our customers have to say about their experience.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <Reveal
                key={index}
                direction={index % 2 === 0 ? 'right' : 'left'}
                delay={index * 120}
              >
                <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed mb-6 relative pl-6">
                    <span className="text-5xl text-violet-500 absolute left-0 top-0 font-serif">"</span>
                    {testimonial.quote}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-violet-600 to-blue-500 rounded-full flex items-center justify-center font-bold">
                      {testimonial.initials}
                    </div>
                    <div>
                      <div className="font-semibold">{testimonial.author}</div>
                      <div className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section (odd #7 — transparent, stagnant abstract bg shows through) */}
      <section className="relative z-10 py-24 overflow-hidden">
        <Parallax speed={0.6} clamp={340} className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="w-[600px] h-[600px] bg-violet-500/20 rounded-full blur-3xl" />
        </Parallax>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <Reveal direction="up">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Ready to Transform Your Influencer Marketing?
            </h2>
          </Reveal>
          <Reveal direction="up" delay={120}>
            <p className="text-gray-400 text-lg mb-10">
              Join thousands of brands and agencies already using kol.my to scale their influencer campaigns.
            </p>
          </Reveal>

          <Reveal direction="up" delay={200}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="px-8 py-4 text-base font-semibold bg-gradient-to-r from-violet-600 to-blue-500 rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/30 inline-flex items-center justify-center gap-2"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/login"
                className="px-8 py-4 text-base font-semibold border border-white/10 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center"
              >
                Contact Sales
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
